<template>
  <v-container fluid class="pa-6">
    <!-- Header Section with Date Filter -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="filter-card elevation-0" outlined>
          <v-card-text class="pa-4">
            <v-row align="center">
              <v-col cols="12" md="6">
                <h2 class="section-title mb-2">Reporte de Caja</h2>
                <p class="text-caption grey--text">
                  Resumen de ventas y formas de pago del díA
                </p>
              </v-col>
              <v-col cols="12" :md="canSelectCajero ? 3 : 6">
                <v-text-field
                  v-model="issueDate"
                  label="Fecha del reporte"
                  outlined
                  dense
                  type="date"
                  :rules="f_required"
                  prepend-inner-icon="mdi-calendar"
                  @change="get_report"
                  hide-details="auto"
                  :loading="loading_status"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" v-if="canSelectCajero">
                <v-select
                  v-model="selectedCajero"
                  :items="cajerosOptions"
                  item-text="name"
                  item-value="code"
                  label="Cajero"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-account"
                  @change="get_report"
                ></v-select>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  icon
                  color="primary"
                  @click="get_report"
                  :loading="loading_status"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6">
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="success">mdi-cash-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Venta Total</div>
              <div class="kpi-value">{{ formatCurrency(totalSales) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small :color="salesTrend.color">{{ salesTrend.icon }}</v-icon>
                {{ salesTrend.text }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="primary">mdi-receipt</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Transacciones</div>
              <div class="kpi-value">{{ totalTransactions }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="info">mdi-file-document-multiple</v-icon>
                Documentos emitidos
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="warning">mdi-cart</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Ticket Promedio</div>
              <div class="kpi-value">{{ formatCurrency(averageTicket) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Por transacción</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="info">mdi-credit-card</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Método Principal</div>
              <div class="kpi-value text-truncate">{{ topPaymentMethod.name }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                {{ topPaymentMethod.percentage }}% del total
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Summary Cards -->
    <v-row class="mb-4">
      <!-- Document Types Summary -->
      <v-col cols="12" lg="6">
        <v-row>
          <v-col cols="12">
            <v-card class="summary-card elevation-0" outlined>
              <v-card-title class="pa-0">
                <v-toolbar flat class="minimal-toolbar">
                  <v-icon class="mr-2" color="success">mdi-file-document-multiple</v-icon>
                  <h3 class="card-title">Por Tipo de Documento</h3>
                </v-toolbar>
              </v-card-title>

              <v-data-table
                :headers="headersDocType"
                :items="docType"
                :items-per-page="5"
                hide-default-footer
                class="minimal-table"
                :loading="loading_status"
                dense
              >
                <template v-slot:[`item.label`]="{ item }">
                  <v-chip small :color="getDocColor(item.label)" dark>
                    {{ getDocLabel(item.label) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.qty`]="{ item }">
                  <span class="font-weight-medium">{{ item.qty }}</span>
                </template>

                <template v-slot:[`item.value`]="{ item }">
                  <span class="font-weight-medium">{{ formatCurrency(item.value) }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="summary-card elevation-0" outlined>
              <v-card-title class="pa-0">
                <v-toolbar flat class="minimal-toolbar">
                  <v-icon class="mr-2" color="info">mdi-account-cash</v-icon>
                  <h3 class="card-title">Por Vendedor</h3>
                </v-toolbar>
              </v-card-title>

              <v-data-table
                :headers="headersDocType"
                :items="Vendors"
                :items-per-page="5"
                hide-default-footer
                class="minimal-table"
                :loading="loading_status"
                dense
              >
                <template v-slot:[`item.label`]="{ item }">
                  <div class="vendor-info">
                    <v-avatar size="32" color="primary" class="mr-2">
                      <span class="white--text text-caption">
                        {{ getInitials(item.label) }}
                      </span>
                    </v-avatar>
                    <span class="text-truncate">{{ item.label }}</span>
                  </div>
                </template>

                <template v-slot:[`item.qty`]="{ item }">
                  <span class="font-weight-medium">{{ item.qty }}</span>
                </template>

                <template v-slot:[`item.value`]="{ item }">
                  <span class="font-weight-medium">{{ formatCurrency(item.value) }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <!-- Payment Methods Summary -->
      <v-col cols="12" lg="6">
        <v-card class="summary-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon class="mr-2" color="primary">mdi-cash-register</v-icon>
              <h3 class="card-title">Formas de Pago</h3>
              <v-spacer></v-spacer>
              <v-chip small label> {{ cash_type.length }} tipos </v-chip>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :headers="headers_cash"
            :items="cash_type"
            :items-per-page="5"
            hide-default-footer
            class="minimal-table"
            :loading="loading_status"
            loading-text="Cargando..."
            dense
          >
            <template v-slot:[`item.label`]="{ item }">
              <div class="payment-method">
                <v-icon small class="mr-2" :color="getPaymentColor(item.label)">
                  {{ getPaymentIcon(item.label) }}
                </v-icon>
                {{ item.label }}
              </div>
            </template>

            <template v-slot:[`item.qty`]="{ item }">
              <v-chip x-small label>{{ item.qty }}</v-chip>
            </template>

            <template v-slot:[`item.value`]="{ item }">
              <div class="text-right">
                <div class="font-weight-medium">{{ formatCurrency(item.value) }}</div>
                <div class="text-caption grey--text">
                  {{ getPercentage(item.value) }}%
                </div>
              </div>
            </template>

            <template v-slot:body.append>
              <tr class="grey lighten-4 font-weight-bold">
                <td>TOTAL</td>
                <td class="text-center">{{ sumTable("cash_type", "qty") }}</td>
                <td class="text-right">{{ formatCurrency(totalSales) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Documents Table -->
    <v-card class="main-card elevation-0" outlined>
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title" :class="{ 'text-h6': $vuetify.breakpoint.mobile }">
            Documentos del Día
          </h2>
          <v-spacer></v-spacer>
          <v-btn icon color="primary" @click="openPrintDrawer" class="d-none d-sm-flex">
            <v-icon>mdi-printer</v-icon>
          </v-btn>
          <v-btn icon color="success" @click="downloadExcel" class="d-none d-sm-flex">
            <v-icon>mdi-microsoft-excel</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Desktop Table -->
      <template v-if="$vuetify.breakpoint.mdAndUp">
        <v-data-table
          :headers="headers_docs"
          :items="cash_data"
          :items-per-page="10"
          item-key="id"
          sort-by="issuedate"
          sort-desc
          class="minimal-table clickable-rows"
          :loading="loading_status_docs"
          loading-text="Cargando documentos..."
          @click:row="get_e_doc"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
          }"
        >
          <template v-slot:[`item.issuedate`]="{ item }">
            <div class="date-time">
              <div>{{ formatDate(item.issuedate) }}</div>
              <div class="text-caption grey--text">{{ formatTime(item.issuedate) }}</div>
            </div>
          </template>

          <template v-slot:[`item.doc_number`]="{ item }">
            <div class="document-info">
              <div class="font-weight-medium">{{ item.doc_number }}</div>
              <v-chip x-small :color="getDocColor(item.doc_type)" dark label>
                {{ getDocLabel(item.doc_type) }}
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.invoice`]="{ item }">
            <div class="invoice-info">
              <span v-if="item.invoice">{{ item.invoice }}</span>
              <span v-else class="grey--text">-</span>
            </div>
          </template>

          <template v-slot:[`item.doc_value`]="{ item }">
            <div class="text-right font-weight-medium">
              {{ formatCurrency(item.doc_value) }}
            </div>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <div class="status-badges">
              <v-tooltip bottom v-if="item.dian_id">
                <template v-slot:activator="{ on }">
                  <v-icon small color="success" v-on="on" class="mr-1">
                    mdi-check-circle
                  </v-icon>
                </template>
                <span>Enviado a DIAN</span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.cufe">
                <template v-slot:activator="{ on }">
                  <v-icon small color="primary" v-on="on" class="mr-1">
                    mdi-shield-check
                  </v-icon>
                </template>
                <span>CUFE válido</span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.cufe_n">
                <template v-slot:activator="{ on }">
                  <v-icon small color="warning" v-on="on"> mdi-file-undo </v-icon>
                </template>
                <span>Nota de crédito</span>
              </v-tooltip>
            </div>
          </template>

          <!-- Empty State -->
          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon size="48" color="grey lighten-2">mdi-receipt</v-icon>
              <div class="text-h6 mt-2 grey--text">No hay documentos para esta fecha</div>
              <div class="text-caption grey--text">
                Selecciona otra fecha para ver documentos
              </div>
            </div>
          </template>
        </v-data-table>
      </template>

      <!-- Mobile List -->
      <template v-else>
        <v-list v-if="cash_data.length > 0" class="py-0">
          <template v-for="(item, index) in cash_data">
            <v-list-item :key="item.id" @click="get_e_doc(item)" class="px-4 py-3">
              <v-list-item-content>
                <div class="d-flex justify-space-between align-start mb-2">
                  <div class="font-weight-medium">{{ item.doc_number }}</div>
                  <v-chip x-small :color="getDocColor(item.doc_type)" dark label>
                    {{ getDocLabel(item.doc_type) }}
                  </v-chip>
                  <div class="text-right">
                    <div class="font-weight-bold">
                      {{ formatCurrency(item.doc_value) }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ formatTime(item.issuedate) }}
                    </div>
                  </div>
                </div>

                <div class="d-flex align-center mt-2">
                  <div class="text-caption grey--text mr-3">
                    {{ formatDate(item.issuedate) }}
                  </div>
                  <div v-if="item.invoice" class="text-caption grey--text">
                    Fact: {{ item.invoice }}
                  </div>
                  <v-spacer></v-spacer>
                  <div class="status-badges">
                    <v-icon v-if="item.dian_id" x-small color="success" class="mr-1">
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-if="item.cufe" x-small color="primary" class="mr-1">
                      mdi-shield-check
                    </v-icon>
                    <v-icon v-if="item.cufe_n" x-small color="warning">
                      mdi-file-undo
                    </v-icon>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="index < cash_data.length - 1" :key="'div-' + index" />
          </template>
        </v-list>

        <!-- Empty State Mobile -->
        <div v-else-if="!loading_status_docs" class="text-center py-12">
          <v-icon size="64" color="grey lighten-2">mdi-receipt</v-icon>
          <h3 class="text-h6 mt-4 grey--text">No hay documentos</h3>
          <p class="grey--text mt-2">Selecciona otra fecha</p>
        </div>
      </template>
    </v-card>

    <!-- Document Viewer Dialog -->
    <docViewer :item="item" :dialog="dialog" @close="dialog = false" @refresh="refresh" />

    <!-- Cash Report Printer -->
    <CashReportPrinter
      :drawer.sync="printDrawer"
      :report-data="printReportData"
      @printed="onReportPrinted"
      @print-error="onPrintError"
    />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "@/services/webserver.js";
import { getDocumentTypeName } from "@/utils/documentTypes.js";
import docViewer from "@/components/doc_viewer.vue";
import CashReportPrinter from "@/components/CashReportPrinter.vue";
import createDoc from "@/utils/create_doc.js";
import * as XLSX from "xlsx";

export default {
  name: "CashReport",
  components: { docViewer, CashReportPrinter },
  data() {
    return {
      // Validation
      f_required: [(v) => !!v || "Campo requerido"],

      // Loading states
      loading_status: false,
      loading_status_docs: false,

      // Date
      issueDate: getToday(),

      // Cajeros
      cajeros: [],
      selectedCajero: "",

      // Data
      cash_data: [],
      cash_type: [],
      docType: [],
      Vendors: [],

      // Dialog
      dialog: false,
      item: createDoc(),

      // Print Drawer
      printDrawer: false,

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Headers
      headers_cash: [
        {
          text: "Forma de pago",
          value: "label",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cant.",
          value: "qty",
          align: "center",
          width: "20%",
        },
        {
          text: "Valor",
          value: "value",
          align: "right",
          width: "30%",
        },
      ],

      headersDocType: [
        {
          text: "Tipo",
          value: "label",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cant.",
          value: "qty",
          align: "center",
          width: "20%",
        },
        {
          text: "Valor",
          value: "value",
          align: "right",
          width: "30%",
        },
      ],

      headers_docs: [
        {
          text: "Fecha/Hora",
          value: "issuedate",
          width: "20%",
        },
        {
          text: "Documento",
          value: "doc_number",
          width: "25%",
        },
        {
          text: "Tipo",
          value: "doc_type",
          width: "15%",
        },
        {
          text: "Factura",
          value: "invoice",
          width: "15%",
        },
        {
          text: "Valor",
          value: "doc_value",
          align: "right",
          width: "15%",
        },
        {
          text: "Estado",
          value: "status",
          align: "center",
          sortable: false,
          width: "10%",
        },
      ],
    };
  },

  computed: {
    // KPI Calculations
    totalSales() {
      return this.cash_type.reduce((sum, item) => sum + parseFloat(item.value || 0), 0);
    },

    totalTransactions() {
      return this.cash_data.length;
    },

    averageTicket() {
      if (this.totalTransactions === 0) return 0;
      return this.totalSales / this.totalTransactions;
    },

    canSelectCajero() {
      const access = this.$store.getters.profile.user_access;
      return access === "ADMIN" || access === "Analista";
    },
    cajerosOptions() {
      return [{ code: "", name: "Todos los cajeros" }, ...this.cajeros];
    },
    topPaymentMethod() {
      if (this.cash_type.length === 0) {
        return { name: "-", percentage: 0 };
      }
      const top = this.cash_type[0];
      const percentage =
        this.totalSales > 0 ? Math.round((top.value / this.totalSales) * 100) : 0;
      return {
        name: top.label,
        percentage,
      };
    },

    salesTrend() {
      // This would compare with previous day in real implementation
      // For now, showing a static trend
      return {
        icon: "mdi-trending-up",
        color: "success",
        text: "vs día anterior",
      };
    },

    printReportData() {
      return {
        date: this.issueDate,
        totalSales: this.totalSales,
        totalTransactions: this.totalTransactions,
        averageTicket: this.averageTicket,
        paymentMethods: this.cash_type,
        documentTypes: this.docType,
        vendors: this.Vendors,
        documents: this.cash_data,
      };
    },
  },

  mounted() {
    if (this.canSelectCajero) {
      this.loadCajeros();
    }
    this.get_report();
  },

  methods: {
    // Formatting methods
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    // Utility methods
    getPercentage(value) {
      if (this.totalSales === 0) return 0;
      return Math.round((value / this.totalSales) * 100);
    },

    getInitials(name) {
      if (!name) return "?";
      const parts = name.split(/[@.\s]+/);
      return parts[0].substring(0, 2).toUpperCase();
    },

    getPaymentIcon(payment) {
      const icons = {
        EFECTIVO: "mdi-cash",
        TARJETA: "mdi-credit-card",
        TRANSFERENCIA: "mdi-bank-transfer",
        CREDITO: "mdi-calendar-clock",
        CHEQUE: "mdi-checkbook",
      };
      const key = Object.keys(icons).find((k) => payment.toUpperCase().includes(k));
      return icons[key] || "mdi-cash-multiple";
    },

    getPaymentColor(payment) {
      const colors = {
        EFECTIVO: "success",
        TARJETA: "primary",
        TRANSFERENCIA: "info",
        CREDITO: "warning",
        CHEQUE: "secondary",
      };
      const key = Object.keys(colors).find((k) => payment.toUpperCase().includes(k));
      return colors[key] || "grey";
    },

    getDocLabel(docType) {
      return getDocumentTypeName(docType);
    },

    getDocColor(docType) {
      const colors = {
        TK: "info",
        FS: "success",
        FC: "primary",
        FE: "secondary",
        NC: "warning",
      };
      return colors[docType] || "grey";
    },

    // Message handling
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Data methods
    sumTable(table, key) {
      return this[table]
        .reduce((sum, item) => sum + parseFloat(item[key] || 0), 0)
        .toLocaleString("es");
    },

    downloadExcel() {
      try {
        const exportData = this.cash_data.map((item) => ({
          Fecha: this.formatDate(item.issuedate),
          Hora: this.formatTime(item.issuedate),
          Documento: item.doc_number,
          Tipo: this.getDocLabel(item.doc_type),
          Factura: item.invoice || "-",
          Valor: item.doc_value,
          ZIP: item.dian_id ? "Sí" : "No",
          CUFE: item.cufe ? "Sí" : "No",
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reporte de Caja");
        XLSX.writeFile(wb, `reporte_caja_${this.issueDate}.xlsx`);
        this.showMessage("Archivo Excel descargado", "success");
      } catch (error) {
        this.showMessage("Error al descargar Excel", "error");
      }
    },

    // API methods
    async get_report() {
      this.loading_status = true;

      const userId = this.canSelectCajero
        ? this.selectedCajero || null
        : window.profile.user_email;
      const qry = {
        store: window.store.store_id,
        date: this.issueDate,
        userId: userId,
      };
      console.log("Query para obtener reporte:", qry);

      try {
        const response = await new Promise((resolve, reject) => {
          webserver("get_cashdrawer_report", qry, resolve, reject);
        });
        console.log("Respuesta del servidor:", response);

        // Verificar si la respuesta contiene error
        if (response && response.error) {
          throw new Error(response.message || "Error en la respuesta del servidor");
        }

        // Procesar los datos de pagos
        this.processPaymentData(response.payments || []);
        this.loading_status = false;

        // Load documents after payment data
        this.get_docs();
      } catch (error) {
        this.loading_status = false;
        console.error("Error en get_report:", error);
        this.showMessage(`Error al cargar reporte: ${error.message}`, "error");
      }
    },

    processPaymentData(data) {
      // Filtrar datos nulos o inválidos
      const validData = data.filter(
        (payment) =>
          payment &&
          payment.payment_concept &&
          payment.doc_type &&
          payment.userId &&
          payment.payment_value !== null &&
          payment.payment_value !== undefined
      );

      // Process payment methods
      const paymentResult = [];
      const paymentMap = {};

      validData.forEach((payment) => {
        const key = payment.payment_concept || "Sin definir";
        if (!paymentMap[key]) {
          paymentMap[key] = { label: key, qty: 0, value: 0 };
          paymentResult.push(paymentMap[key]);
        }
        paymentMap[key].qty += 1;
        paymentMap[key].value += parseFloat(payment.payment_value || 0);
      });

      // Sort by value descending
      paymentResult.sort((a, b) => b.value - a.value);
      this.cash_type = paymentResult;

      // Process document types
      const docResult = [];
      const docMap = {};

      validData.forEach((payment) => {
        const key = payment.doc_type || "Sin definir";
        if (!docMap[key]) {
          docMap[key] = { label: key, qty: 0, value: 0 };
          docResult.push(docMap[key]);
        }
        docMap[key].qty += 1;
        docMap[key].value += parseFloat(payment.payment_value || 0);
      });

      this.docType = docResult;

      // Process vendors
      const vendorResult = [];
      const vendorMap = {};

      validData.forEach((payment) => {
        const key = payment.userId || "Usuario desconocido";
        if (!vendorMap[key]) {
          vendorMap[key] = { label: key, qty: 0, value: 0 };
          vendorResult.push(vendorMap[key]);
        }
        vendorMap[key].qty += 1;
        vendorMap[key].value += parseFloat(payment.payment_value || 0);
      });

      vendorResult.sort((a, b) => b.value - a.value);
      this.Vendors = vendorResult;
    },
    async get_docs() {
      this.loading_status_docs = true;

      const qry = {
        store: window.store.store_id,
        table: "docs",
        filters: [
          {
            field: "doc_type",
            operator: " IN ",
            value: "'TK','FS','FC','FE','NC'", // Corregido
          },
          {
            field: "DATE(issuedate)",
            operator: "=",
            value: this.issueDate,
          },
          {
            field: "store",
            operator: "=",
            value: window.store.store_id,
          },
        ],
      };

      // Add user filter: USUARIO siempre filtra por su email, ADMIN/Analista solo si seleccionaron un cajero
      if (this.$store.getters.profile.user_access === "USUARIO") {
        qry.filters.push({
          field: "userId",
          operator: "=",
          value: this.$store.state.profile.email,
        });
      } else if (this.selectedCajero) {
        qry.filters.push({
          field: "userId",
          operator: "=",
          value: this.selectedCajero,
        });
      }

      try {
        const data = await new Promise((resolve, reject) => {
          webserver("get_dataTable", qry, resolve, reject);
        });

        // Verificar si la respuesta contiene error
        if (data && data.error) {
          throw new Error(data.message || "Error en la respuesta del servidor");
        }

        this.cash_data = data || [];
        this.loading_status_docs = false;
      } catch (error) {
        this.loading_status_docs = false;
        console.error("Error en get_docs:", error);
        this.showMessage(`Error al cargar documentos: ${error.message}`, "error");
      }
    },

    get_e_doc(item) {
      const issudate = item.issuedate.split(" ");
      const doc = {
        store: item.store,
        issueDate: issudate[0],
        did: item.doc_number,
      };

      this.loading_status = true;
      webserver(
        "get_invoice_fe",
        doc,
        (data) => {
          this.loading_status = false;
          this.item = data;
          this.dialog = true;
        },
        () => {
          this.loading_status = false;
          this.showMessage("Error al cargar documento", "error");
        }
      );
    },

    loadCajeros() {
      webserver("get_cajeros", { store: window.store.store_id }, (data) => {
        if (data && !data.error) {
          this.cajeros = Array.isArray(data) ? data : [];
        }
      });
    },

    refresh() {
      this.get_report();
    },

    openPrintDrawer() {
      this.printDrawer = true;
    },

    onReportPrinted() {
      this.showMessage("Reporte impreso correctamente", "success");
      this.printDrawer = false;
    },

    onPrintError(error) {
      this.showMessage("Error al imprimir: " + error.message, "error");
    },
  },
};
</script>

<style scoped>
/* Filter Card */
.filter-card {
  border: 1px solid #e0e0e0;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Summary Cards */
.summary-card {
  border: 1px solid #e0e0e0;
  height: 100%;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

/* Clickable rows */
.clickable-rows >>> tbody tr {
  cursor: pointer;
}

/* Custom cells */
.payment-method {
  display: flex;
  align-items: center;
}

.vendor-info {
  display: flex;
  align-items: center;
  max-width: 200px;
}

.document-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-time {
  display: flex;
  flex-direction: column;
}

.status-badges {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Text utilities */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 600px) {
  .kpi-value {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .card-title {
    font-size: 1rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    padding: 12px 16px !important;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}
</style>
